import { Autocomplete, Fade, Grid, TextField, Typography, useTheme } from '@mui/material';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import AnalyticsService from '../../../../shared/services/analytics/analytics.service';
import { buildGenericSeriesStack, valueFormatter } from '../../../../shared/interfaces/analytics';
import { stackedRowTotals } from '../../../../shared/utils/analytics-utils';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils'; import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import EmissionService from '../../../../shared/services/emission/emission.service';
import { IEmission } from '../../../../shared/services/emission/emission.model';
import ProjectService from '../../../../shared/services/project/project.service';
import { IProject } from '../../../../shared/services/project/project.model';
import { GraphBarChartStacked } from '../graphs/graph-bar-chart-stacked';
import { GraphDonutChart } from '../graphs/graph-donut-chart';
import { DrillDownDrawer } from '../drill-down-drawer';
import { useDrillDown } from '../use-drill-down';
import { useDrillDimensionLabel } from '../use-drill-dimension-label';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay';
import { AnalyticsByProjectSkeleton } from './analytics-by-project-skeleton';
import { EmissionsTable } from '../../emissons-components/overview/emissions-table';

export const AnalyticsByProject: FunctionComponent<{
  reportPeriodId: string;
  emissionType: 'marketBased' | 'locationBased';
  setLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
  /** When set, the breakdown donut carries the comparison period as an inner ring. */
  comparisonPeriod?: { id: string; label: string };
  periodLabel?: string;
}> = ({ reportPeriodId, emissionType, setLoaded, comparisonPeriod, periodLabel }) => {
  // Services
  const analyticsService = new AnalyticsService();
  const emissionService = new EmissionService();
  const projectService = ProjectService.getInstance();
  // Context
  const reportPeriodContext = useContext(ReportPeriodContext);
  const screenLabelsContext = useContext(ScreenLabelsContext);
  // State
  const [pieProjectEmissions, setPieProjectEmissions] = useState<any>();
  const [comparisonPieProjectEmissions, setComparisonPieProjectEmissions] = useState<any>();
  const [stackedProjectEmissions, setStackedProjectEmissions] = useState<any>();
  const [comparisonStackedProjectEmissions, setComparisonStackedProjectEmissions] = useState<any>();
  const [emissionRows, setEmissionRows] = useState<IEmission[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [allProjects, setAllProjects] = useState<IProject[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState<string | undefined>();
  const [order, setOrder] = useState<'asc' | 'desc' | undefined>();
  // Colors
  const theme = useTheme();
  const colors = getMixedColorPalette(theme.palette.primary.main);
  // Loading
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const showSkeleton = useSkeletonDelay(isLoading, 250);
  // Variables
  const requiredReportPeriodId = reportPeriodId || reportPeriodContext?.lastSelectedReportId;
  // Click-to-drill: a stale selection clears when the period, basis or comparison changes.
  const scopeTwoMethod = emissionType === 'marketBased' ? 'market' : 'location';
  const drill = useDrillDown([requiredReportPeriodId, emissionType, comparisonPeriod?.id]);
  const dimLabel = useDrillDimensionLabel();
  // The stacked history keys its series by entity label only; the same-dimension donut carries the
  // ids, so resolve a clicked series label back to its project id through this map.
  const projectIdByLabel = useMemo(() => {
    const map = new Map<string, string>();
    (pieProjectEmissions?.[emissionType] || []).forEach((item: any) => {
      if (item?.id != null) map.set(String(item.label), String(item.id));
    });
    return map;
  }, [pieProjectEmissions, emissionType]);

  useEffect(() => {
    if (!requiredReportPeriodId) {
      setIsLoading(false);
      if (setLoaded) setLoaded(true);
      return;
    }

    setIsLoading(true);
    const expectedRequests = comparisonPeriod ? 5 : 3;
    let completedRequests = 0;
    const checkLoadingStatus = () => {
      completedRequests += 1;
      if (completedRequests === expectedRequests) {
        setIsLoading(false);
        if (setLoaded) setLoaded(true);
      }
    };

    analyticsService
      .getPieData(requiredReportPeriodId, `?data=project&topN=all`)
      .then((response) => {
        setPieProjectEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    if (comparisonPeriod) {
      analyticsService
        .getPieData(comparisonPeriod.id, `?data=project&topN=all`)
        .then((response) => {
          setComparisonPieProjectEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonPieProjectEmissions(undefined);
          checkLoadingStatus();
        });

      analyticsService
        .getStackedData(comparisonPeriod.id, `?data=project&topN=all`)
        .then((response) => {
          setComparisonStackedProjectEmissions(response);
          checkLoadingStatus();
        })
        .catch((err) => {
          console.log(err);
          setComparisonStackedProjectEmissions(undefined);
          checkLoadingStatus();
        });
    } else {
      setComparisonPieProjectEmissions(undefined);
      setComparisonStackedProjectEmissions(undefined);
    }

    analyticsService
      .getStackedData(requiredReportPeriodId, `?data=project&topN=all`)
      .then((response) => {
        setStackedProjectEmissions(response);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });

    emissionService
      .getByDomainHighest(
        requiredReportPeriodId,
        'project',
        0,
        perPage,
        orderBy,
        order,
      )
      .then((emissionsResponse) => {
        const projectId = emissionsResponse.rows[0]?.projectId ?? '';
        setSelectedProjectId(projectId);
        setEmissionRows(emissionsResponse.rows);
        setTotalRecords(emissionsResponse.count);
        checkLoadingStatus();
      })
      .catch((err) => {
        console.log(err);
        checkLoadingStatus();
      });
  }, [requiredReportPeriodId, comparisonPeriod?.id]);

  useEffect(() => {
    projectService
      .getAll()
      .then(setAllProjects)
      .catch((err) => console.log(err));
  }, []);

  const getEmissionsForProject = (
    reportPeriodIdParam: string,
    projectId: string,
    page: number,
  ) => {
    if (!reportPeriodIdParam || !projectId) return;
    emissionService
      .getAll(
        reportPeriodIdParam,
        page,
        perPage,
        orderBy,
        order,
        `&projectId=${projectId}`,
      )
      .then((response) => {
        setEmissionRows(response.rows);
        setTotalRecords(response.count);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (requiredReportPeriodId) {
      getEmissionsForProject(requiredReportPeriodId, selectedProjectId, inputPage);
    }
  }, [inputPage, perPage, orderBy, order]);

  return (
    <>
      {isLoading && (
        <Fade in={showSkeleton} unmountOnExit>
          <div>
            <AnalyticsByProjectSkeleton />
          </div>
        </Fade>
      )}

      {!isLoading && (
        <Fade in={!showSkeleton} unmountOnExit>
          <div>
            <Grid container spacing={2}>
              {pieProjectEmissions && pieProjectEmissions[emissionType] && (
                <Grid
                  item
                  xl={4}
                  lg={4}
                  md={12}
                  sm={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphDonutChart
                    enableTopN
                    defaultTopN={5}
                    title={`GHG Emission Breakdown by ${screenLabelsContext.getLabel(
                      'project',
                      { plural: false, capitalize: 'first' },
                    )}`}
                    tooltip={`The greenhouse gas emission breakdown per ${screenLabelsContext.getLabel(
                      'project',
                      { plural: false },
                    )} for the selected report period`}
                    dataset={pieProjectEmissions[emissionType]}
                    colors={colors}
                    periodLabel={periodLabel}
                    comparison={comparisonPeriod && comparisonPieProjectEmissions?.[emissionType]
                      ? { dataset: comparisonPieProjectEmissions[emissionType], label: comparisonPeriod.label }
                      : undefined}
                    onSelect={(item) => drill.openDrill({ dimension: 'project', id: item.id, label: item.label, value: item.value })}
                  />
                </Grid>
              )}

              {stackedProjectEmissions && stackedProjectEmissions[emissionType] && (
                <Grid
                  item
                  xl={8}
                  lg={8}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <GraphBarChartStacked
                    enableTopN
                    defaultTopN={10}
                    title={`GHG Emission History for ${screenLabelsContext.getLabel(
                      'project',
                      { plural: true, capitalize: 'first' },
                    )}`}
                    tooltip={`The greenhouse gas emission history for each month in the selected report period per ${screenLabelsContext.getLabel(
                      'project',
                      { plural: false },
                    )}`}
                    dataset={stackedProjectEmissions[emissionType]}
                    series={buildGenericSeriesStack(
                      stackedProjectEmissions[emissionType],
                      valueFormatter,
                    )}
                    colors={colors}
                    comparison={comparisonPeriod && comparisonStackedProjectEmissions?.[emissionType]
                      ? {
                        totals: stackedRowTotals(comparisonStackedProjectEmissions[emissionType]),
                        label: comparisonPeriod.label,
                        valueFormatter,
                      }
                      : undefined}
                    onSelect={(seriesKey) => {
                      const id = projectIdByLabel.get(seriesKey);
                      if (id) drill.openDrill({ dimension: 'project', id, label: seriesKey });
                    }}
                  />
                </Grid>
              )}

              <Grid
                item
                xl={12}
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Grid container>
                  <Grid item lg={8} sm={12}>
                    <Typography variant="subtitle1" sx={{ flex: 1, mb: 1 }}>
                      <b>
                        Emission Records by{' '}
                        {screenLabelsContext.getLabel('project', {
                          plural: false,
                          capitalize: 'first',
                        })}
                      </b>
                    </Typography>
                  </Grid>
                  <Grid item lg={4} sm={12}>
                    {allProjects.length > 0 && (
                      <Autocomplete
                        id="project-filter-autocomplete"
                        options={[...allProjects].sort((a, b) =>
                          a.summary.localeCompare(b.summary),
                        )}
                        getOptionLabel={(option) => option.summary ?? ''}
                        value={
                          allProjects.find((p) => p.id === selectedProjectId) ?? null
                        }
                        onChange={(_, value) => {
                          const id = value?.id ?? '';
                          setSelectedProjectId(id);
                          if (id !== selectedProjectId) setInputPage(0);
                          if (requiredReportPeriodId)
                            getEmissionsForProject(requiredReportPeriodId, id, 0);
                        }}
                        size="small"
                        sx={{ width: '100%', mb: 2 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Filter"
                            variant="outlined"
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
                <EmissionsTable
                  emissions={emissionRows}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  setOrderBy={setOrderBy}
                  setOrder={setOrder}
                  backLink={'/analytics/dashboard?tab=emissions&dim=project'}
                  backText="Back to analytics"
                />
              </Grid>
            </Grid>
          </div>
        </Fade>
      )}

      <DrillDownDrawer
        open={drill.open}
        onClose={drill.close}
        selection={drill.selection}
        reportPeriodId={requiredReportPeriodId || ''}
        periodLabel={periodLabel}
        scopeTwoMethod={scopeTwoMethod}
        comparisonReportPeriodId={comparisonPeriod?.id}
        dimensionLabel={dimLabel(drill.selection?.dimension)}
      />
    </>
  );
};
